import React, { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import ReactLoader from 'components/Loader';
import { useHistory } from 'react-router-dom';
import { Container, Row, Card, CardHeader, CardBody, Col } from 'reactstrap';
import ReactButton from 'components/Buttons/ReactButton';
import { FlexControllerV2 } from 'components/FlexControllerInput/V2';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { useServiceLevels } from 'hooks/useServiceLevels';

const LevelServiceConfiguration = () => {
  const { control, handleSubmit, setValue, errors, watch } = useForm({
    defaultValues: {
      service_level_telephony: '',
      service_level_monitoring_queue_chat: '',
      telephony_projected_time: '',
      telephony_acceptable_percentage: '',
      telephony_acceptable_percentage_non_conformities: '',
      chat_projected_time: '',
      chat_acceptable_percentage: '',
      chat_acceptable_percentage_non_conformities: ''
    }
  });

  const watchTelephonyAcceptable = watch('telephony_acceptable_percentage');
  const watchChatAcceptable = watch('chat_acceptable_percentage');

  useEffect(() => {
    const val = Number(watchTelephonyAcceptable);
    if (!isNaN(val) && val >= 0 && val <= 100) {
      setValue('telephony_acceptable_percentage_non_conformities', 100 - val);
    }
  }, [watchTelephonyAcceptable, setValue]);

  useEffect(() => {
    const val = Number(watchChatAcceptable);
    if (!isNaN(val) && val >= 0 && val <= 100) {
      setValue('chat_acceptable_percentage_non_conformities', 100 - val);
    }
  }, [watchChatAcceptable, setValue]);

  const history = useHistory();

  const {
    groupedByType: serviceLevelsByType,
    isLoading,
    update: updateServiceLevel,
    create: createServiceLevel
  } = useServiceLevels();

  const telephonyRecord = serviceLevelsByType?.TELEPHONY?.[0];
  const chatRecord = serviceLevelsByType?.MONITORING_QUEUE_CHAT?.[0];

  const projectedTimeOptions = [
    { label: '4 semanas (1 mês)', value: '4' },
    { label: '8 semanas (2 meses)', value: '8' },
    { label: '12 semanas (3 meses)', value: '12' },
    { label: '24 semanas (6 meses)', value: '24' }
  ];

  useEffect(() => {
    if (serviceLevelsByType?.MONITORING_QUEUE_CHAT?.length) {
      const chatData = serviceLevelsByType.MONITORING_QUEUE_CHAT[0];
      setValue('service_level_monitoring_queue_chat', chatData.levelOfService);

      const selectedOption = projectedTimeOptions.find(
        (opt) => Number(opt.value) === Number(chatData.projectedTime)
      );
      setValue('chat_projected_time', selectedOption || null);

      setValue('chat_acceptable_percentage', chatData.acceptablePercentage);
      setValue(
        'chat_acceptable_percentage_non_conformities',
        chatData.acceptablePercentageOfNonConformities
      );
    }

    if (serviceLevelsByType?.TELEPHONY?.length) {
      const telephonyData = serviceLevelsByType.TELEPHONY[0];
      setValue('service_level_telephony', telephonyData.levelOfService);

      const selectedOption = projectedTimeOptions.find(
        (opt) => Number(opt.value) === Number(telephonyData.projectedTime)
      );
      setValue('telephony_projected_time', selectedOption || null);

      setValue(
        'telephony_acceptable_percentage',
        telephonyData.acceptablePercentage
      );
      setValue(
        'telephony_acceptable_percentage_non_conformities',
        telephonyData.acceptablePercentageOfNonConformities
      );
    }
  }, [serviceLevelsByType, setValue]);

  const onSubmit = useCallback(
    async (formData) => {
      try {
        const telephonyValue = Number(formData.service_level_telephony);
        const chatValue = Number(formData.service_level_monitoring_queue_chat);

        const getSelectValue = (val) => {
          if (val === null || val === undefined || val === '') return null;
          if (typeof val === 'object' && 'value' in val) {
            return val.value ? Number(val.value) : null;
          }
          return Number(val);
        };

        const telephonyPayload = {
          levelOfService: telephonyValue,
          projectedTime: getSelectValue(formData.telephony_projected_time),
          acceptablePercentage: Number(
            formData.telephony_acceptable_percentage
          ),
          acceptablePercentageOfNonConformities: Number(
            formData.telephony_acceptable_percentage_non_conformities
          )
        };

        if (!telephonyRecord) {
          await createServiceLevel({
            ...telephonyPayload,
            target: 'TELEPHONY'
          });
        } else {
          await updateServiceLevel({
            id: telephonyRecord.id,
            ...telephonyPayload
          });
        }

        const chatPayload = {
          levelOfService: chatValue,
          projectedTime: getSelectValue(formData.chat_projected_time),
          acceptablePercentage: Number(formData.chat_acceptable_percentage),
          acceptablePercentageOfNonConformities: Number(
            formData.chat_acceptable_percentage_non_conformities
          )
        };

        if (!chatRecord) {
          await createServiceLevel({
            ...chatPayload,
            target: 'MONITORING_QUEUE_CHAT'
          });
        } else {
          await updateServiceLevel({
            id: chatRecord.id,
            ...chatPayload
          });
        }

        toast.success('Configurações atualizadas com sucesso!');
      } catch (error) {
        toast.error('Erro ao salvar as configurações');
      }
    },
    [updateServiceLevel, createServiceLevel, telephonyRecord, chatRecord]
  );

  return (
    <>
      <SimpleHeader name="Form validation" parentName="Forms" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3> Configuração do tempo de nível de serviço geral</h3>
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <div className="needs-validation">
                      <h4 className="text-muted text-uppercase mb-3">
                        Telefonia
                      </h4>
                      <Row className="mb-4">
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="service_level_telephony"
                            label="Tempo nível de serviço (s)"
                            required
                            rules={{
                              required: 'Campo obrigatório',
                              pattern: {
                                value: /^[1-9]\d*$/,
                                message: 'Apenas números inteiros positivos'
                              }
                            }}
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="select"
                            name="telephony_projected_time"
                            label="Tempo projetado"
                            options={projectedTimeOptions}
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="telephony_acceptable_percentage"
                            label="% Aceitável (Conforme)"
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="telephony_acceptable_percentage_non_conformities"
                            label="% Aceitável (Não Conforme)"
                            disabled={true}
                            errors={errors}
                          />
                        </Col>
                      </Row>

                      <hr />

                      <h4 className="text-muted text-uppercase mb-3">Chat</h4>
                      <Row className="mb-3">
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="service_level_monitoring_queue_chat"
                            label="Tempo nível de serviço (s)"
                            required
                            rules={{
                              required: 'Campo obrigatório',
                              pattern: {
                                value: /^[1-9]\d*$/,
                                message: 'Apenas números inteiros positivos'
                              }
                            }}
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="select"
                            name="chat_projected_time"
                            label="Tempo projetado"
                            options={projectedTimeOptions}
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="chat_acceptable_percentage"
                            label="% Aceitável (Conforme)"
                            errors={errors}
                          />
                        </Col>
                        <Col md="3">
                          <FlexControllerV2
                            control={control}
                            type="number"
                            name="chat_acceptable_percentage_non_conformities"
                            label="% Aceitável (Não Conforme)"
                            disabled={true}
                            errors={errors}
                          />
                        </Col>
                      </Row>

                      <div className="mt-3">
                        <ReactButton
                          btnColor="confirmation"
                          onClick={handleSubmit(onSubmit)}
                        >
                          Salvar
                        </ReactButton>
                        <ReactButton
                          btnColor="cancelation"
                          onClick={() => history.push('/admin/configuration')}
                        >
                          Cancelar
                        </ReactButton>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default LevelServiceConfiguration;
